import { useEffect, useRef, useState } from "react";

import type { TextStepModel } from "../../api/thinking";
import type { Telemetry } from "../../telemetry/posthog";
import { answerProblem } from "./answerRules";
import { formatClock, useCountdown } from "./useCountdown";
import { useStepTelemetry } from "./useStepTelemetry";

type Props = {
  step: TextStepModel;
  question: string | null;
  telemetry: Telemetry;
  busy: boolean;
  onSubmit: (text: string, autoSubmitted: boolean) => void;
};

function Timer({ remaining, enforced }: { remaining: number | null; enforced: boolean }) {
  if (remaining === null) return null;
  if (!enforced) {
    return (
      <p className="timer soft" aria-live="off">
        Suggested time left: {formatClock(remaining)} (not enforced)
      </p>
    );
  }
  return (
    <p className={`timer ${remaining < 15_000 ? "urgent" : ""}`} role="timer" aria-live="polite">
      Time left: {formatClock(remaining)}. Your answer is submitted automatically at 0:00.
    </p>
  );
}

export function TextStepView({ step, question, telemetry, busy, onSubmit }: Props) {
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const sentGuard = useRef(false); // synchronous guard: a click and the timer can race
  const textRef = useRef(text);
  useEffect(() => {
    textRef.current = text;
  }, [text]);
  const { onKeyDown, onPaste, submitted } = useStepTelemetry(telemetry, question);

  const send = (auto: boolean) => {
    if (sentGuard.current) return;
    sentGuard.current = true;
    setSent(true);
    submitted(textRef.current.length, auto);
    onSubmit(textRef.current, auto);
  };
  // Typed answers need real text to submit by hand; the timer running out still submits as is.
  const problem = answerProblem(text, step);
  const remaining = useCountdown(
    step.seconds,
    step.elapsed_ms,
    step.enforced ? () => send(true) : undefined,
  );

  return (
    <section>
      <h1>{step.title}</h1>
      {step.context && <blockquote className="context">{step.context}</blockquote>}
      <label htmlFor="answer" className="prompt">
        {step.prompt}
      </label>
      <Timer remaining={remaining} enforced={step.enforced} />
      <textarea
        id="answer"
        rows={8}
        maxLength={step.char_limit}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        disabled={busy || sent}
      />
      <p className="muted counter">
        {text.length} / {step.char_limit}
        {step.min_chars > 0 && ` · at least ${step.min_chars}`}
      </p>
      <button
        type="button"
        disabled={busy || sent || problem !== null}
        aria-describedby={problem ? "answer-hint" : undefined}
        onClick={() => send(false)}
      >
        Submit answer
      </button>
      {problem && text.trim() && (
        <p id="answer-hint" className="muted" role="status">
          {problem}
        </p>
      )}
      <p className="muted">Once submitted, you can't go back to change it.</p>
    </section>
  );
}
